import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Building2, Search, MapPin, AlertCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCnpj = (raw) => {
  const d = String(raw || '').replace(/\D/g, '').slice(0, 14);
  if (d.length !== 14) return raw;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const EmpresaBusca = () => {
  const [q, setQ] = useState('');
  const [uf, setUf] = useState('');
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    setItems(null);
    try {
      const params = { q: q.trim() };
      if (uf) params.uf = uf.toUpperCase();
      const resp = await axios.get(`${API}/public/empresa/busca`, { params, timeout: 15000 });
      setItems(resp.data.items || []);
      if (resp.data.message) setMessage(resp.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível buscar agora. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="empresa-busca-page">
      <div className="text-center py-2">
        <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-3">
          <Building2 className="w-7 h-7 text-[#006030]" />
        </div>
        <h1 className="text-2xl md:text-3xl font-black text-[#0F1C2E] tracking-tight">
          Dossiê da empresa
        </h1>
        <p className="text-sm text-[#475569] mt-1 max-w-xl mx-auto">
          Descubra quanto uma empresa já recebeu do poder público. Informe o CNPJ com 14 dígitos.
        </p>
      </div>

      <Card className="p-4 md:p-5 border border-[#E2E8F0]">
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
          <Input
            type="text"
            placeholder="CNPJ ou nome da empresa"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1"
            data-testid="empresa-search-input"
            minLength={3}
            required
          />
          <Input
            type="text"
            placeholder="UF"
            value={uf}
            onChange={(e) => setUf(e.target.value.toUpperCase().slice(0, 2))}
            className="sm:w-24"
            data-testid="empresa-uf-input"
            maxLength={2}
          />
          <Button
            type="submit"
            disabled={loading || q.trim().length < 3}
            className="bg-[#006030] hover:bg-[#004020] text-white"
            data-testid="empresa-search-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />}
            Buscar
          </Button>
        </form>
        <p className="text-[11px] text-[#94A3B8] mt-2">
          Dica. A busca por CNPJ retorna dados imediatos. A busca por nome está em preparação.
        </p>
      </Card>

      {message && (
        <div
          className="text-sm text-[#92400E] bg-[#FEF3C7] border border-[#FDE68A] rounded-md p-3"
          data-testid="empresa-busca-message"
        >
          {message}
        </div>
      )}

      {error && (
        <div
          className="flex items-start gap-2 text-sm text-[#C62828] bg-[#FEE2E2] border border-[#FCA5A5] rounded-md p-3"
          data-testid="empresa-busca-error"
        >
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {items && items.length > 0 && (
        <div className="space-y-3" data-testid="empresa-results">
          {items.map((it) => (
            <Card
              key={it.cnpj}
              onClick={() => navigate(`/empresa/${it.cnpj}`)}
              className="p-4 border border-[#E2E8F0] cursor-pointer hover:border-[#006030] hover:shadow-md transition-all"
              data-testid={`empresa-result-${it.cnpj}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#E8F5E9] flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-[#006030]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#0F1C2E] truncate">
                    {it.nome_fantasia || it.razao_social}
                  </p>
                  <p className="text-xs text-[#475569] truncate">
                    {formatCnpj(it.cnpj)} · {it.tipo}
                  </p>
                  {(it.municipio || it.uf) && (
                    <p className="text-[11px] text-[#94A3B8] flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" /> {it.municipio}
                      {it.uf ? ` / ${it.uf}` : ''}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {items && items.length === 0 && !message && (
        <div className="text-center py-8 text-sm text-[#94A3B8]" data-testid="empresa-empty">
          Nenhum resultado encontrado.
        </div>
      )}
    </div>
  );
};

export default EmpresaBusca;
